
import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Homepage/Footer/Footer";

import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  PersonOutline,
  AdminPanelSettingsOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import Navbar from "../components/Homepage/Navbar/Navbar";

const API_URL ="https://rukap.edu.in/leave-api";

export default function LeaveLogin() {
  const [loginType, setLoginType] = useState("EMPLOYEE");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { username, password, loginType }
      );

      const { token, user } = response.data;

      const adminRoles = [
        "HOD",
        "PRINCIPAL",
        "REGISTRAR",
        "ADMIN",
      ];

      const isAdmin = adminRoles.includes(user.role);

      if (
        (loginType === "EMPLOYEE" && user.role !== "EMPLOYEE") ||
        (loginType === "ADMIN" && !isAdmin)
      ) {
        setError("You are not authorized for this login.");
        return;
      }

      sessionStorage.setItem("leaveToken", token);
      sessionStorage.setItem("leaveUser", JSON.stringify(user));

      const routes = {
        EMPLOYEE: "/leave-portal/employee-dashboard",
        HOD: "/leave-portal/admin-dashboard",
        PRINCIPAL: "/leave-portal/admin-dashboard",
        REGISTRAR: "/leave-portal/admin-dashboard",
        ADMIN: "/leave-portal/admin-dashboard",
      };

      navigate(routes[user.role]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Login failed. Please check your username and password."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          background: "#f2f5f9",
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: "100%",
            maxWidth: 450,
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              bgcolor: "#0B3C5D",
              color: "white",
              textAlign: "center",
              p: 3,
            }}
          >
            <AdminPanelSettingsOutlined sx={{ fontSize: 48 }} />

            <Typography variant="h5" fontWeight={700}>
              Leave Management Portal
            </Typography>

            <Typography variant="body2" sx={{ mt: 1 }}>
              Rayalaseema University, Kurnool
            </Typography>
          </Box>

          <Tabs
            value={loginType}
            onChange={(event, value) => {
              setLoginType(value);
              setError("");
            }}
            variant="fullWidth"
          >
            <Tab
              value="EMPLOYEE"
              icon={<PersonOutline />}
              iconPosition="start"
              label="Employee"
            />

            <Tab
              value="ADMIN"
              icon={<AdminPanelSettingsOutlined />}
              iconPosition="start"
              label="Administrator"
            />
          </Tabs>

          <Box component="form" onSubmit={handleLogin} sx={{ p: 4 }}>
            <Typography
              variant="h6"
              textAlign="center"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              {loginType === "EMPLOYEE"
                ? "Employee Login"
                : "Administrator Login"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 3 }}
            >
              Sign in with your authorised university account.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              label="Username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              autoComplete="username"
              fullWidth
              required
              margin="normal"
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              fullWidth
              required
              margin="normal"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle password visibility"
                        onClick={() =>
                          setShowPassword((previous) => !previous)
                        }
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                mt: 3,
                py: 1.4,
                bgcolor: "#0B3C5D",
                "&:hover": { bgcolor: "#082b43" },
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="#0B3C5D" />
              ) : (
                "Sign In"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>
      <Footer />  
    </>
  );
}
